import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useMemo, useRef } from "react";
import Swal from "sweetalert2";
import MaterialApi from "../apiProvider/materialapi";
import { toast } from "react-toastify";
import ReactTableComponent from "../table/ReactTableComponent";
import usePermission from "../hook/usePermission";

const MaterialPage = () => {
  const canAdd = usePermission("materials", "add");
  const canEdit = usePermission("materials", "edit");
  const canDelete = usePermission("materials", "delete");
  const havePermissions = canEdit || canDelete;
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    displayOrder: "",
    status: true,
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  // TABLE DATA STATES
  const [materialData, setMaterialData] = useState([]);      // visible rows

  // PAGINATION STATES
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // SEARCH STATES
  const [search, setSearch] = useState("");

  const fetchMaterialList = async () => {
    const res = await MaterialApi.materialList({
      page: pageIndex,
      limit: pageSize,
      filters: { name: search },
    });
    const d = res?.response;
    setMaterialData(d?.data || []);
    setTotalPages(d?.totalPages || 0);
    setTotal(d?.total || 1);
  };

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearch(v);
    setPageIndex(0);
  };

  useEffect(() => {
    fetchMaterialList();
  }, [pageIndex, search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Material Name is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      name: formData.name,
      displayOrder: formData.displayOrder ? parseInt(formData.displayOrder) : 0,
      status: formData.status === "true" || formData.status === true,
    };

    let response;
    if (isEditMode) {
      response = await MaterialApi.updateMaterial(payload, editId);
    } else {
      response = await MaterialApi.materialCreate(payload);
    }

    if (response.status) {
      fetchMaterialList();
      handleModalClose();
    }
  };

  const handleEdit = async (id) => {
    const response = await MaterialApi.materialDetails(id);
    if (response.status) {
      const data = response.response.data;
      setIsEditMode(true);
      setEditId(id);
      setFormData({
        name: data.name,
        displayOrder: data.displayOrder,
        status: data.status,
      });
      setModalVisible(true);
    }
  };

  const handleRemoveRow = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this material?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await MaterialApi.deleteMaterial(id);
          if (response.status) {
            Swal.fire("Deleted!", "Material has been deleted.", "success");
            fetchMaterialList();
          } else {
            Swal.fire("Error!", response.message || "Failed to delete material.", "error");
          }
        } catch (error) {
          Swal.fire("Error!", "Something went wrong.", "error");
        }
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        header: "S.No",
        size: 70,
        id: "sno",
        cell: (info) => pageIndex * pageSize + info.row.index + 1,
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Display Order",
        accessorKey: "displayOrder",
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (info) => (
          <span className={`badge ${info.getValue() ? 'bg-success' : 'bg-danger'}`}>
            {info.getValue() ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      ...(havePermissions
        ? [
          {
            header: "Actions",
            cell: (info) => {
              const id = info.row.original._id;
              return (
                <div className="d-flex gap-2">
                  {canEdit && (
                    <button
                      type="button"
                      className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                      onClick={() => handleEdit(id)}
                    >
                      <Icon icon="lucide:edit" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                      onClick={() => handleRemoveRow(id)}
                    >
                      <Icon icon="fluent:delete-24-regular" />
                    </button>
                  )}
                </div>
              );
            },
          },
        ]
        : []),
    ],
    [pageIndex, canEdit, canDelete, havePermissions]
  );

  const handleNextPage = () => {
    if (pageIndex + 1 < totalPages) setPageIndex((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (pageIndex > 0) setPageIndex((prev) => prev - 1);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setFormData({ name: "", displayOrder: "", status: true });
    setErrors({});
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div style={{ position: "relative" }}>
          <input
            type="text"
            className="form-control radius-8"
            placeholder="Search Material..."
            style={{ maxWidth: 350, minWidth: 200, paddingRight: '40px' }}
            value={search}
            onChange={handleSearchChange}
          />
          <div className="search-icon" style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)" }}>
            <Icon icon="ic:baseline-search" className="icon text-xl line-height-1" />
          </div>
        </div>

        {canAdd && (
          <button
            className="btn btn-success"
            onClick={() => {
              setFormData({ name: "", displayOrder: "", status: true });
              setIsEditMode(false);
              setModalVisible(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
            Create Material
          </button>
        )}
      </div>

      <div className="card-body p-24">
        <ReactTableComponent
          data={materialData}
          columns={columns}
          pageIndex={pageIndex}
          totalPages={totalPages}
          onNextPage={handleNextPage}
          onPreviousPage={handlePreviousPage}
          totalRecords={total}
        />
      </div>

      {modalVisible && (
        <div
          className="modal fade show d-block"
          id="materialModal"
          tabIndex={-1}
          aria-labelledby="materialModalLabel"
          aria-hidden="true"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
        >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border-bottom">
              <h1 className="modal-title fs-5" id="materialModalLabel">
                {isEditMode ? "Edit Material" : "Create Material"}
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleModalClose}
              />
            </div>

            <div className="modal-body p-24">
              <form onSubmit={handleSubmit}>
                <div className="row gy-3">
                  <div className="col-12">
                    <label className="form-label">Material Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      value={formData.name || ""}
                      onChange={handleChange}
                    />
                    {errors.name && <span className="text-danger">{errors.name}</span>}
                  </div>

                  <div className="col-12">
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      name="displayOrder"
                      className="form-control"
                      value={formData.displayOrder || ""}
                      min="1"
                      onKeyDown={(e) => {
                        if (["e", "E", "+", "-", "."].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || parseInt(val, 10) >= 1) {
                          handleChange(e);
                        }
                      }}
                    />
                    {errors.displayOrder && <span className="text-danger">{errors.displayOrder}</span>}
                  </div>

                  <div className="col-12">
                    <label className="form-label">Status</label>
                    <select
                      name="status"
                      className="form-select"
                      value={formData.status.toString()}
                      onChange={handleChange}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>

                  <div className="modal-footer px-0 pb-0 pt-24">
                    <button type="submit" className="btn btn-primary text-sm">
                      {isEditMode ? "Update" : "Submit"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default MaterialPage;
